import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { GOALS, currentGoal, type Goal } from "../village/guide.js";
import { levelsOf, useVillageStore } from "./village.js";
import { useAttackStore } from "./attack.js";
import { useEditorStore } from "./editor.js";
import { useSessionStore } from "./session.js";
import { useSaveStore } from "./save.js";
import { useToastStore } from "./toast.js";
import { claimGoal, fetchClaimedGoals } from "../api/progress.js";
import { ApiProblem } from "../api/client.js";

/**
 * Which steps of the village guide this village has finished (playtest,
 * 29 Sep 2026: "once he finished a stage it won't come back").
 *
 * Steps were already meant to be sticky, but two things undid it:
 *  - they were only ticked while the Village screen was open, so a step
 *    finished on the Map tab (an attack) and undone before coming back (the
 *    army died) was never recorded;
 *  - the record was one list per DEVICE, not per village, so a second village
 *    or a new sign-in on the same phone inherited — or lost — someone
 *    else's progress.
 *
 * Now the ticking runs app-wide (`useGoalTicker` in App) and the record is
 * kept per village. Once a step is in it, nothing takes it out.
 */

const LEGACY_KEY = "empire.goals.done";
const keyOf = (villageId: string): string => `empire.goals.done.${villageId}`;

function read(key: string): string[] {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function write(key: string, ids: readonly string[]): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(ids));
  } catch {
    /* a guide that forgets is better than one that crashes */
  }
}

interface GoalState {
  villageId: string | null;
  ticked: ReadonlySet<string>;
  /** Switch to a village's record (and carry over the old per-device one, once). */
  open: (villageId: string | null) => void;
  tick: (ids: readonly string[]) => void;
}

export const useGoalStore = create<GoalState>((set, get) => ({
  villageId: null,
  ticked: new Set(),

  open: (villageId) => {
    if (villageId === get().villageId) return;
    if (!villageId) {
      set({ villageId: null, ticked: new Set() });
      return;
    }
    const ids = new Set(read(keyOf(villageId)));
    // The old device-wide list belongs to whichever village was played here
    // before this change: hand it to the first one opened, then retire it.
    const legacy = read(LEGACY_KEY);
    if (legacy.length > 0) {
      for (const id of legacy) ids.add(id);
      write(keyOf(villageId), [...ids]);
      try {
        globalThis.localStorage?.removeItem(LEGACY_KEY);
      } catch {
        /* ignore */
      }
    }
    set({ villageId, ticked: ids });
  },

  tick: (ids) => {
    const { villageId, ticked } = get();
    if (!villageId || ids.every((id) => ticked.has(id))) return;
    const next = new Set(ticked);
    for (const id of ids) next.add(id);
    write(keyOf(villageId), [...next]);
    set({ ticked: next });
  },
}));

/** The current step and its number, from the live village and the record. */
export function useCurrentGoal(): { goal: Goal | null; index: number; newlyDone: string[] } {
  const economy = useVillageStore((s) => s.economy);
  const targets = useAttackStore((s) => s.targets);
  const targetsLoaded = useAttackStore((s) => s.targetsLoadedAt !== null);
  // The SAVED defence when there is one: the editor store is borrowed by the
  // tutorial and by defend Trials, and a practice maze there must not tick
  // "Use your new road" (the server checks the saved one before paying).
  const editorLayout = useEditorStore((s) => s.layout);
  const savedLayout = useSaveStore((s) => s.savedLayout);
  const layout = savedLayout ?? editorLayout;
  const ticked = useGoalStore((s) => s.ticked);

  return useMemo(() => {
    if (!economy) return { goal: null, index: GOALS.length, newlyDone: [] };
    return currentGoal(
      { economy, levels: levelsOf(economy), targets: targetsLoaded ? targets : null, layout },
      ticked
    );
  }, [economy, targets, targetsLoaded, layout, ticked]);
}

/**
 * Mounted once, in App: follows the signed-in village and records every step
 * the moment it is done, whichever screen the player is on.
 */
export function useGoalTicker(): void {
  const villageId = useSessionStore((s) => s.villageId);
  const open = useGoalStore((s) => s.open);
  const tick = useGoalStore((s) => s.tick);
  useEffect(() => open(villageId), [villageId, open]);
  const { newlyDone } = useCurrentGoal();
  const ticked = useGoalStore((s) => s.ticked);
  const economyLoaded = useVillageStore((s) => s.economy !== null);
  useEffect(() => {
    if (newlyDone.length > 0) tick(newlyDone);
  }, [newlyDone, tick]);

  // Every ticked step gets its reward claimed, once (29 Sep 2026). The server
  // re-checks the step and pays under a primary key, so a retry is harmless.
  useEffect(() => {
    if (!villageId || !economyLoaded || ticked.size === 0) return;
    void claimTicked(villageId, ticked);
  }, [villageId, economyLoaded, ticked]);
}

const CLAIMED_KEY = (villageId: string): string => `empire.goals.claimed.${villageId}`;
const inFlight = new Set<string>();
let serverChecked: string | null = null;

async function claimTicked(villageId: string, ticked: ReadonlySet<string>): Promise<void> {
  const token = await useSessionStore.getState().token();
  if (!token) return;
  const claimed = new Set(read(CLAIMED_KEY(villageId)));
  // Once per village per app start: learn what the server already paid, so a
  // device that lost its storage does not ask for everything again.
  if (serverChecked !== villageId) {
    try {
      const answer = await fetchClaimedGoals(villageId, token);
      for (const id of answer.claimed) claimed.add(id);
      write(CLAIMED_KEY(villageId), [...claimed]);
      serverChecked = villageId;
    } catch {
      return;
    }
  }
  for (const id of GOALS.map((g) => g.id)) {
    if (!ticked.has(id) || claimed.has(id) || inFlight.has(id)) continue;
    inFlight.add(id);
    try {
      const answer = await claimGoal(villageId, token, id);
      useVillageStore.getState().receive(answer.village);
      if (answer.reward.wood > 0) {
        const title = GOALS.find((g) => g.id === id)?.title ?? "Step";
        useToastStore.getState().show(`${title} ✓  +${answer.reward.wood} of each`);
      }
      claimed.add(id);
    } catch (error) {
      // Not done on the server's books (a 409): remember it, so a refusal is
      // not asked again on every render. Offline: leave it for the next try.
      if (error instanceof ApiProblem && error.status === 409) claimed.add(id);
    } finally {
      inFlight.delete(id);
      write(CLAIMED_KEY(villageId), [...claimed]);
    }
  }
}
